import { useSelector } from "react-redux";
import { store } from "../index";

/**
 * The consult the rail currently has selected.
 *
 * The rail writes the selection to `econsults.consultId`. Before the first switch
 * the chart still carries the census row, where `id` is the consult id and
 * `patientId` is the patient - the same contract `econsultsID` relies on. `id` is
 * only read as a consult id when `patientId` is present, so a patient id can never
 * be sent as a consult id.
 *
 * Returns undefined when no consult is resolved. Callers must then omit consultId
 * entirely, which keeps the server's primary-consult fallback.
 */
const resolveConsultId = (econsults) => {
  const raw =
    econsults?.consultId ??
    (econsults?.patientId != null ? econsults?.id : undefined);
  if (raw === null || raw === undefined || raw === "") return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getSelectedConsultId = () =>
  resolveConsultId(store.getState()?.econsultsReducer?.econsults);

/**
 * The same selection, as a hook.
 *
 * Read-only views (eNotes, the activity log) have to re-fetch when the rail
 * switches consult. They cannot use `getSelectedConsultId`, because an imperative
 * store read does not re-render. Put the returned id in the effect dependency
 * list and the view follows the selected chip.
 *
 * Returns undefined when no consult is resolved, which is the cross-consult view.
 */
export const useSelectedConsultId = () =>
  useSelector((state) => resolveConsultId(state?.econsultsReducer?.econsults));

/**
 * Adds the selected consult id to a request body.
 *
 * An explicit consultId already on the body always wins. When nothing is selected
 * the body is returned unchanged, so the request stays byte-identical to today.
 */
export const withConsultId = (body) => {
  const requestBody = body ?? {};
  if (requestBody.consultId !== null && requestBody.consultId !== undefined) {
    return requestBody;
  }
  const consultId = getSelectedConsultId();
  return consultId === undefined ? requestBody : { ...requestBody, consultId };
};
